import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CategoryFilter } from '../components/CategoryFilter';
import { EventSection } from '../components/EventSection';
import { HeroCarouselV2 } from '../components/HeroCarouselV2';
import { OrganizerCTA } from '../components/OrganizerCTA';
import { SeoHead } from '../components/SeoHead';
import { DEFAULT_SEO_TITLE, DEFAULT_SEO_DESCRIPTION } from '../constants/seoDefaults';
import { matchesSearch } from '../utils/eventUtils';
import { filterMainList, sortLongEventsLast, getOngoingLongEvents } from '@/utils/longEvents';

// ─── Skeletons ────────────────────────────────────────────────────────────

function SectionSkeleton({ count = 3 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl overflow-hidden border border-border/60 bg-card animate-pulse">
          <div className="aspect-video bg-muted" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-3 bg-muted rounded w-1/2" />
            <div className="h-3 bg-muted rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Container único da Landing — Hero e Próximos Eventos usam exatamente essa
// largura/padding, para que as bordas esquerda e direita de todas as
// seções fiquem alinhadas ao rolar a página.
const CONTAINER = 'max-w-[1920px] mx-auto px-4 md:px-8';

// ─── HomePage ─────────────────────────────────────────────────────────────

export function HomePage() {
  // today/featured/upcoming/combinedEvents e searchTerm vêm do PublicLayout
  // (ver PublicLayout.jsx) — o campo de busca mora no header, componente
  // irmão desta página, não pai/filho, então o termo "confirmado" (Enter,
  // ou campo vazio) precisa ser compartilhado por ali em vez de um estado
  // local aqui.
  const { featured, combinedEvents, loading, error, searchTerm } = useOutletContext();

  // Filtro de categoria da barra "Próximos Eventos" — só estado local, sem
  // navegação (igual ao app Flutter): trocar a categoria não muda a URL,
  // não recarrega a página e não mexe no scroll, só re-filtra a lista em
  // memória. null = "Todos".
  const [activeCategory, setActiveCategory] = useState(null);

  // Categoria E busca com AND — mesmo filtro (matchesSearch) usado pelo
  // dropdown de sugestões do header, pra não ter duas lógicas de busca.
  const filteredEvents = combinedEvents.filter((e) =>
    (!activeCategory || e.category === activeCategory) && matchesSearch(e, searchTerm)
  );

  // Eventos longos (ver utils/longEvents.js): sem filtro, os que estão no
  // meio do período saem da lista (ficam na seção "Em cartaz"); com
  // categoria ou busca, todos aparecem, mas os longos vão pro fim.
  const hasFilter = Boolean(activeCategory) || Boolean(searchTerm.trim());
  const visibleEvents = hasFilter
    ? sortLongEventsLast(filteredEvents)
    : filterMainList(filteredEvents);

  const ongoingLongEvents = getOngoingLongEvents(combinedEvents);

  // Composição do hero: prioriza eventos em destaque; sem eles, usa os
  // próximos eventos reais (com a mesma regra de eventos longos da lista
  // principal). Nunca dados fictícios.
  const heroEvents = featured.length ? featured : filterMainList(combinedEvents);

  return (
    <>
      <SeoHead title={DEFAULT_SEO_TITLE} description={DEFAULT_SEO_DESCRIPTION} />

      {/* 1. Hero ─────────────────────────────────────────────────── */}
      {/* Sem overflow-hidden na section: sombras e hover dos cards da
          galeria precisam respirar verticalmente. O recorte horizontal
          (carrossel + corte do card parcial) é feito só dentro do
          viewport do Embla, em HeroCarouselV2. */}
      <section className="pt-10 md:pt-16 pb-16 md:pb-24 bg-background">
        <div className={cn(
          'grid gap-6 lg:gap-10 items-center',
          CONTAINER,
          // Proporção ~40/60 (texto/carrossel) em vez de coluna de texto
          // fixa em px + 1fr: uma coluna fixa deixava o carrossel livre
          // para esticar até a borda do container, mas o wrapper interno
          // do HeroCarouselV2 tinha um max-w fixo bem menor que essa
          // largura disponível — sobrava vão vazio entre o carrossel e a
          // borda direita em telas largas. Com colunas proporcionais (fr)
          // + wrapper em w-full (ver HeroCarouselV2), o carrossel sempre
          // preenche exatamente sua coluna, na mesma largura útil das
          // demais sections.
          (heroEvents.length > 0 || loading) && 'lg:grid-cols-[minmax(320px,2fr)_3fr]'
        )}>

          {/* Esquerda — peça editorial: badge + título + parágrafo + botão,
              com peso visual equivalente ao carrossel (não é mais um
              complemento fininho ao lado). -mt-3: compensa a faixa de dots
              abaixo do carrossel (mt-5 + altura dos dots), que não tem
              equivalente deste lado — sem isso, o items-center do grid
              centraliza o texto ~13px abaixo do centro real do card
              principal. */}
          <div className="flex flex-col gap-6 max-w-xl lg:max-w-none -mt-3">
            <div className="inline-flex items-center gap-2 w-fit px-3.5 py-1.5 rounded-full bg-orange-50 text-orange-600">
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-sm font-medium">Eventos para todos os momentos</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-[-0.02em] leading-[1.05]">
              Os melhores<br />
              eventos da<br />
              sua cidade.<br />
              <span className="text-orange-500">Hoje</span> e sempre.
            </h1>

            <p className="text-muted-foreground text-lg leading-relaxed max-w-md">
              Descubra shows, festivais, gastronomia, esportes e experiências incríveis na sua região.
            </p>

            <Button asChild size="lg" className="rounded-full px-10 h-14 text-base w-fit">
              <a href="#proximos-eventos">
                Explorar eventos
                <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
          </div>

          {/* Direita — mesmo HeroCarouselV2/Embla, só reposicionado dentro
              da nova composição. Sangra até a borda da tela. */}
          {(loading || heroEvents.length > 0) && (
            <HeroCarouselV2 events={heroEvents} loading={loading} />
          )}

        </div>
      </section>

      {/* ── Erro global ──────────────────────────────────────────── */}
      {error && (
        <div className={cn(CONTAINER, 'py-6')}>
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* 2. Próximos Eventos (inclui eventos acontecendo agora) ──── */}
      <section id="proximos-eventos" className="py-14 bg-muted/40">
        <div className={CONTAINER}>
          {loading ? (
            <>
              <div className="h-6 bg-muted rounded w-44 mb-6 animate-pulse" />
              <SectionSkeleton count={6} />
            </>
          ) : (
            <EventSection
              title="Próximos Eventos"
              events={visibleEvents}
              filters={<CategoryFilter active={activeCategory} onChange={setActiveCategory} />}
              emptyMessage={
                activeCategory || searchTerm.trim()
                  ? 'Nenhum evento encontrado.'
                  : 'Nenhum evento cadastrado ainda.'
              }
            />
          )}
        </div>
      </section>

      {/* 3. Em cartaz — eventos longos em andamento, sem limite ───── */}
      {!loading && ongoingLongEvents.length > 0 && (
        <section id="em-cartaz" className="py-14 bg-background">
          <div className={CONTAINER}>
            <EventSection title="Em cartaz" events={ongoingLongEvents} />
          </div>
        </section>
      )}

      {/* 4. CTA final — organizadores + baixar o app, mesmo fundo ─── */}
      <OrganizerCTA />
    </>
  );
}
